import React, { useMemo, useRef, useState } from "react";
import type {
  AnalyticsSurface,
  OperationsDailyActivity,
  OperationsDashboardData,
} from "../../operationsAnalytics";
import { filterOperationsDashboard } from "../../operationsAnalytics";

const CHART_DAYS = 60;
const CHART_WIDTH = 960;
const CHART_HEIGHT = 320;
const PLOT = { left: 52, right: 18, top: 20, bottom: 48 };

export function nearestChartIndex(
  x: number,
  width: number,
  pointCount: number,
): number {
  if (pointCount <= 1 || width <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, x / width));
  return Math.round(ratio * (pointCount - 1));
}

function Metric({
  label,
  value,
  detail,
}: {
  label: string;
  value: number;
  detail: string;
}) {
  return (
    <article className="ops__metric">
      <h2>{label}</h2>
      <strong>{value.toLocaleString()}</strong>
      <p>{detail}</p>
    </article>
  );
}

function formatDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

function ActivityChart({
  days,
  metric,
  metricLabel,
}: {
  days: OperationsDailyActivity[];
  metric: "plays" | "logins" | "activePlayers";
  metricLabel: string;
}) {
  const visibleDays = useMemo(() => days.slice(-CHART_DAYS), [days]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const plotWidth = CHART_WIDTH - PLOT.left - PLOT.right;
  const plotHeight = CHART_HEIGHT - PLOT.top - PLOT.bottom;
  const maxValue = Math.max(...visibleDays.map((day) => day[metric]), 1);
  const xAt = (index: number) =>
    PLOT.left +
    (visibleDays.length <= 1 ? plotWidth / 2 : (index / (visibleDays.length - 1)) * plotWidth);
  const yAt = (value: number) => PLOT.top + plotHeight - (value / maxValue) * plotHeight;

  if (visibleDays.length === 0) {
    return <p className="ops__empty">No activity data yet.</p>;
  }

  const selected = selectedIndex === null ? null : visibleDays[selectedIndex];
  const selectFromPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const plotLeft = (PLOT.left / CHART_WIDTH) * rect.width;
    const plotPixelWidth = (plotWidth / CHART_WIDTH) * rect.width;
    setSelectedIndex(
      nearestChartIndex(event.clientX - rect.left - plotLeft, plotPixelWidth, visibleDays.length),
    );
  };
  const points = visibleDays
    .map((day, index) => `${xAt(index).toFixed(1)},${yAt(day[metric]).toFixed(1)}`)
    .join(" ");
  const firstDate = visibleDays[0].date;
  const lastDate = visibleDays.at(-1)!.date;

  return (
    <div className="opschart">
      <div className="opschart__hint">
        Hover, touch, or focus the chart for details
      </div>
      <div className="opschart__sr" aria-live="polite">
        {selected
          ? `${formatDate(selected.date)}: ${selected[metric].toLocaleString()} ${metricLabel}`
          : ""}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        role="img"
        tabIndex={0}
        aria-label={
          selected
            ? `${formatDate(selected.date)}: ${selected[metric]} ${metricLabel}`
            : `${metricLabel} from ${formatDate(firstDate)} through ${formatDate(lastDate)}`
        }
        onFocus={() => setSelectedIndex((current) => current ?? visibleDays.length - 1)}
        onBlur={() => setSelectedIndex(null)}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          selectFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.pointerType === "mouse" || event.currentTarget.hasPointerCapture(event.pointerId)) {
            selectFromPointer(event);
          }
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") setSelectedIndex(null);
        }}
        onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          if (event.key === "Home") setSelectedIndex(0);
          else if (event.key === "End") setSelectedIndex(visibleDays.length - 1);
          else {
            const direction = event.key === "ArrowLeft" ? -1 : 1;
            setSelectedIndex((current) =>
              Math.max(0, Math.min(visibleDays.length - 1, (current ?? visibleDays.length - 1) + direction)),
            );
          }
        }}
      >
        <line
          className="opschart__axis"
          x1={PLOT.left}
          y1={PLOT.top}
          x2={PLOT.left}
          y2={PLOT.top + plotHeight}
        />
        <line
          className="opschart__axis"
          x1={PLOT.left}
          y1={PLOT.top + plotHeight}
          x2={CHART_WIDTH - PLOT.right}
          y2={PLOT.top + plotHeight}
        />
        <text x={PLOT.left - 10} y={PLOT.top + 5} textAnchor="end">
          {maxValue}
        </text>
        <text x={PLOT.left - 10} y={PLOT.top + plotHeight + 5} textAnchor="end">
          0
        </text>
        <text x={PLOT.left} y={CHART_HEIGHT - 12}>
          {formatDate(firstDate)}
        </text>
        <text x={CHART_WIDTH - PLOT.right} y={CHART_HEIGHT - 12} textAnchor="end">
          {formatDate(lastDate)}
        </text>
        <polyline className="opschart__line" points={points} />
        {selected && selectedIndex !== null && (
          <>
            <line
              className="opschart__crosshair"
              x1={xAt(selectedIndex)}
              y1={PLOT.top}
              x2={xAt(selectedIndex)}
              y2={PLOT.top + plotHeight}
            />
            <circle
              className="opschart__point"
              cx={xAt(selectedIndex)}
              cy={yAt(selected[metric])}
              r="6"
            />
            <g
              className="opschart__callout"
              transform={`translate(${
                selectedIndex > visibleDays.length / 2
                  ? xAt(selectedIndex) - 180
                  : xAt(selectedIndex) + 12
              } ${Math.max(PLOT.top + 8, yAt(selected[metric]) - 62)})`}
            >
              <rect width="168" height="52" rx="8" />
              <text x="12" y="21">
                {formatDate(selected.date)}
              </text>
              <text className="opschart__callout-value" x="12" y="41">
                {selected[metric].toLocaleString()} {metricLabel}
              </text>
            </g>
          </>
        )}
      </svg>
    </div>
  );
}

function surfaceLabel(surface: AnalyticsSurface): string {
  switch (surface) {
    case "ios_app":
      return "iOS app";
    case "android_app":
      return "Android app";
    case "web":
      return "Web";
    case "unknown":
      return "Unknown";
  }
}

export function Dashboard({ data }: { data: OperationsDashboardData }) {
  const [surface, setSurface] = useState<AnalyticsSurface | "all">("all");
  const [appVersion, setAppVersion] = useState<string | "all">("all");
  const [webVersion, setWebVersion] = useState<string | "all">("all");
  const filtered = useMemo(
    () =>
      filterOperationsDashboard(data, {
        surface,
        appVersion,
        webVersion,
      }),
    [appVersion, data, surface, webVersion],
  );
  const todayBreakdown =
    filtered.dailyActivity.find((day) => day.date === filtered.today)?.byClient ?? [];
  const selectionLabel =
    surface === "all" && appVersion === "all" && webVersion === "all"
      ? "All clients"
      : [
          surface === "all" ? "All surfaces" : surfaceLabel(surface),
          appVersion === "all"
            ? "all app versions"
            : `app ${appVersion}`,
          webVersion === "all"
            ? "all web versions"
            : `web ${webVersion}`,
        ].join(", ");

  return (
    <>
      <div className="ops__meta">
        <span>Seattle day: {filtered.today}</span>
        <span>Updated {new Date(filtered.generatedAt).toLocaleString()}</span>
      </div>
      <section className="ops__filters" aria-labelledby="ops-client-filters">
        <div>
          <h2 id="ops-client-filters">Client dimensions</h2>
          <p>Filter play and login metrics. Unknown includes legacy or invalid metadata.</p>
        </div>
        <div className="ops__filter-fields">
          <label>
            Surface
            <select
              value={surface}
              onChange={(event) =>
                setSurface(event.target.value as AnalyticsSurface | "all")
              }
            >
              <option value="all">All surfaces</option>
              {data.availableSurfaces.map((option) => (
                <option key={option} value={option}>
                  {surfaceLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <label>
            App version
            <select
              value={appVersion}
              onChange={(event) => setAppVersion(event.target.value)}
            >
              <option value="all">All versions</option>
              {data.availableAppVersions.map((option) => (
                <option key={option} value={option}>
                  {option === "unknown" ? "Unknown" : option}
                </option>
              ))}
            </select>
          </label>
          <label>
            Web version
            <select
              value={webVersion}
              onChange={(event) => setWebVersion(event.target.value)}
            >
              <option value="all">All web versions</option>
              {data.availableWebVersions.map((option) => (
                <option key={option} value={option}>
                  {option === "unknown" ? "Unknown" : option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>
      <section className="ops__metrics" aria-label="Operational summary">
        <Metric
          label="Plays today"
          value={filtered.playsToday}
          detail={`${filtered.totalPlays.toLocaleString()} all time - ${selectionLabel}`}
        />
        <Metric
          label="Logins today"
          value={filtered.loginsToday}
          detail={`${filtered.totalLogins.toLocaleString()} selected - ${data.uniqueLoggedInPlayers.toLocaleString()} unique overall`}
        />
        <Metric
          label="Active players"
          value={data.activePlayers}
          detail={`All clients, last 7 days - ${data.registeredPlayers.toLocaleString()} registered`}
        />
        <Metric
          label="Future puzzle days"
          value={data.futurePuzzleDays}
          detail={data.puzzlesThrough ? `Authored through ${data.puzzlesThrough}` : "No future puzzles found"}
        />
      </section>
      <section className="ops__breakdown" aria-labelledby="ops-today-breakdown">
        <div className="ops__section-heading">
          <h2 id="ops-today-breakdown">Today by client</h2>
          <p>{selectionLabel}</p>
        </div>
        {todayBreakdown.length > 0 ? (
          <div
            className="ops__table-wrap"
            role="region"
            aria-label="Today's client activity breakdown"
            tabIndex={0}
          >
            <table>
              <thead>
                <tr>
                  <th scope="col">Surface</th>
                  <th scope="col">App version</th>
                  <th scope="col">Web version</th>
                  <th scope="col">Plays</th>
                  <th scope="col">Logins</th>
                </tr>
              </thead>
              <tbody>
                {todayBreakdown.map((row) => (
                  <tr
                    key={`${row.surface}:${row.appVersion}:${row.webVersion}`}
                  >
                    <th scope="row">{surfaceLabel(row.surface)}</th>
                    <td>{row.appVersion === "unknown" ? "Unknown" : row.appVersion}</td>
                    <td>{row.webVersion === "unknown" ? "Unknown" : row.webVersion}</td>
                    <td>{row.plays.toLocaleString()}</td>
                    <td>{row.logins.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="ops__empty ops__empty--compact">
            No activity matches these client filters today.
          </p>
        )}
      </section>
      <section className="ops__chart">
        <div className="ops__section-heading">
          <h2>Plays per day</h2>
          <p>{selectionLabel}</p>
        </div>
        <ActivityChart days={filtered.dailyActivity} metric="plays" metricLabel="plays" />
      </section>
      <section className="ops__chart">
        <div className="ops__section-heading">
          <h2>Logins per day</h2>
          <p>{selectionLabel}</p>
        </div>
        <ActivityChart days={filtered.dailyActivity} metric="logins" metricLabel="logins" />
      </section>
      <section className="ops__chart">
        <h2>7-day active players</h2>
        <ActivityChart
          days={data.dailyActivity}
          metric="activePlayers"
          metricLabel="active players"
        />
      </section>
      <p className="ops__note">
        Each active-player point counts unique players who submitted on that Seattle day or the
        previous six and is not affected by the client filters. Finalized activity uses private
        daily aggregates; legacy activity without valid metadata appears under Unknown.
      </p>
    </>
  );
}
